
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Grid2X2, Menu, X, User, ShoppingCart } from 'lucide-react';
import Logo from '../ui/Logo';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import CategorySidebar from './CategorySidebar';
import { Input } from '@/components/ui/input';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import CartButton from '../cart/CartButton';
import { useAuth } from '@/contexts/AuthContext';
import AuthDialog from '../auth/AuthDialog';
import LanguageSelector from '../language/LanguageSelector';
import TranslatedText from '@/TranslatedText';

const topNavLinks = [
  { name: 'Home', path: '/' },
  { name: 'Community', path: '/community' },
  { name: 'Promotion', path: '/promotion' },
  { name: 'Sell Portal', path: '/sell-portal' },
  { name: 'Support', path: '/support' },
  { name: 'News & Events', path: '/news-events' }
];

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  const handleSearchClick = () => {
    navigate('/search');
  };
  
  return (
    <header className="bg-ahavex-blue w-full fixed top-0 left-0 right-0 z-[100]">
      <div className="w-full py-3 px-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-shrink-0">
            <Link to="/">
              <Logo size="sm" />
            </Link>
          </div>
          
          <div className="flex-grow max-w-sm mx-2 bg-white rounded-full overflow-hidden">
            <div className="flex items-center px-4 py-px cursor-pointer" onClick={handleSearchClick}>
              <Input 
                type="text" 
                placeholder="Search Ah..." 
                className="border-0 focus:ring-0 text-sm py-1 px-0 cursor-pointer pointer-events-none" 
                aria-label="Search products"
                readOnly
              />
              <Search size={18} className="text-gray-500 flex-shrink-0" />
            </div>
          </div>
          
          <div className="flex items-center gap-5">
            {/* Only show language selector in top nav on desktop */}
            {!isMobile && (
              <LanguageSelector className="text-white" iconColor="white" />
            )}
            
            <div className="relative cursor-pointer">
              <AuthDialog 
                userType="ahavexer"
                icon={Grid2X2}
                title="Ahavexer Portal"
                description="Access exclusive features for Ahavex members"
                color="white"
                className="text-white"
              >
                <Grid2X2 
                  size={20} 
                  strokeWidth={1.5} 
                  color="white" 
                />
              </AuthDialog>
            </div>
            
            <div className="relative cursor-pointer">
              <AuthDialog 
                userType="customer"
                icon={User}
                title="Customer Account"
                description="Sign in to your customer account to track orders and manage preferences"
                color="white"
                className="text-white"
              >
                <User 
                  size={20} 
                  strokeWidth={1.5} 
                  color="white" 
                />
              </AuthDialog>
            </div>
            
            <CartButton 
              className="text-white" 
              iconSize={20} 
              iconColor="white" 
              asButton={false} 
            />
          </div>
        </div>
      </div>
      
      {!isMobile && (
        <nav className="bg-white border-t border-gray-200 w-full">
          <div className="max-w-screen-2xl mx-auto px-8">
            <div className="flex justify-end items-center h-[48px]">
              <div className="flex h-full">
                {topNavLinks.map(link => (
                  <Link 
                    key={link.name} 
                    to={link.path} 
                    className="nav-link text-ahavex-blue flex items-center h-full px-4 hover:text-ahavex-gold transition-colors"
                  >
                    <TranslatedText text={link.name} />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>
      )}
      
      {isMobile && (
        <div className="bg-white py-3 px-4 flex justify-between items-center">
          <Drawer>
            <DrawerTrigger asChild>
              <button className="text-ahavex-blue z-10" aria-label="Open categories menu">
                <Menu size={24} strokeWidth={2} />
              </button>
            </DrawerTrigger>
            <DrawerContent className="p-0 max-h-[80vh]">
              <div className="h-full overflow-y-auto">
                <CategorySidebar className="h-full rounded-none shadow-none" />
              </div>
            </DrawerContent>
          </Drawer>
          
          <div className="flex items-center">
            <div className="text-ahavex-blue text-xl font-bold mr-2">AHAVEX</div>
            <LanguageSelector className="text-ahavex-blue" iconColor="#003274" />
          </div>
          
          <Drawer>
            <DrawerTrigger asChild>
              <button className="text-ahavex-blue z-10" aria-label="Open navigation menu">
                <Menu size={24} strokeWidth={2} />
              </button>
            </DrawerTrigger>
            <DrawerContent className="p-0 max-h-[80vh]">
              <div className="h-full overflow-y-auto">
                <div className="p-4 flex items-center border-b border-ahavex-darkblue">
                  <Logo />
                  <div className="ml-2">
                    <div className="text-ahavex-blue text-lg font-bold">AHAVEX</div>
                    <div className="text-ahavex-blue text-xs">Uplifting African Businesses</div>
                  </div>
                  <DrawerTrigger asChild className="ml-auto">
                    <button className="text-ahavex-blue" aria-label="Close menu">
                      <X size={20} />
                    </button>
                  </DrawerTrigger>
                </div>
                
                <nav className="p-4">
                  <ul className="space-y-4">
                    {topNavLinks.map(link => (
                      <li key={link.name}>
                        <Link to={link.path} className="text-ahavex-blue text-sm block py-2">
                          <TranslatedText text={link.name} />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      )}
    </header>
  );
};

export default Navbar;
